import { CreatorHubSystemEventName, DashboardConversationSystemEventNames } from '@fluenceur/types';
import {
  Archive,
  ChatBubbleEmpty,
  ChatBubbleQuestion,
  Check,
  CheckCircle,
  Coins,
  DataTransferBoth,
  DeliveryTruck,
  Gift,
  Hourglass,
  InfoCircle,
  Package,
  Page,
  Prohibition,
  Undo,
  UserPlus,
  XmarkCircle,
} from 'iconoir-react';
import type { ComponentType, SVGProps } from 'react';

export type SystemEventGlyph = ComponentType<SVGProps<SVGSVGElement>>;

/** Every marker the registry card renders. The mention is out: it has its own card. */
type GlyphedSystemEvent = Exclude<
  (typeof DashboardConversationSystemEventNames)[number],
  typeof CreatorHubSystemEventName.MENTION_RECEIVED
>;

/**
 * One glyph per marker name. The registry is monochrome on purpose: every card
 * wears the same frame, pastille and ink, whatever the marker says. The glyph
 * is the only thing that varies, and it carries no colour of its own.
 *
 * Exhaustive by type: a new marker name fails this build until it gets a glyph,
 * which is the whole point of naming them in one shared place.
 */
const SYSTEM_EVENT_GLYPHS: Record<GlyphedSystemEvent, SystemEventGlyph> = {
  [CreatorHubSystemEventName.CONVERSATION_CREATED]: ChatBubbleEmpty,
  [CreatorHubSystemEventName.CONVERSATION_ARCHIVED]: Archive,
  [CreatorHubSystemEventName.CONVERSATION_UNARCHIVED]: Undo,
  [CreatorHubSystemEventName.CONVERSATION_BLOCKED]: Prohibition,
  [CreatorHubSystemEventName.CONVERSATION_UNBLOCKED]: Check,
  [CreatorHubSystemEventName.CHANNEL_CHANGED]: DataTransferBoth,
  [CreatorHubSystemEventName.COLLAB_BRIEF_ACCEPTED]: Page,
  [CreatorHubSystemEventName.COLLAB_PAYMENT_DUE]: Coins,
  [CreatorHubSystemEventName.COLLAB_PROPOSED]: ChatBubbleQuestion,
  [CreatorHubSystemEventName.BRIEF_APPLICATION_SUBMITTED]: Page,
  [CreatorHubSystemEventName.PROGRAM_APPLICATION_SUBMITTED]: UserPlus,
  [CreatorHubSystemEventName.GIFT_OFFERED]: Gift,
  [CreatorHubSystemEventName.GIFT_CLAIMED]: CheckCircle,
  [CreatorHubSystemEventName.GIFT_SHIPPED]: DeliveryTruck,
  [CreatorHubSystemEventName.GIFT_DELIVERED]: Package,
  [CreatorHubSystemEventName.GIFT_EXPIRED]: Hourglass,
  [CreatorHubSystemEventName.GIFT_WITHDRAWN]: XmarkCircle,
  [CreatorHubSystemEventName.PRODUCT_RECEPTION_QUESTION]: Package,
  [CreatorHubSystemEventName.PRODUCT_RECEPTION_COUNTED]: Package,
};

const GIFT_MARKERS: readonly string[] = [
  CreatorHubSystemEventName.GIFT_OFFERED,
  CreatorHubSystemEventName.GIFT_CLAIMED,
  CreatorHubSystemEventName.GIFT_SHIPPED,
  CreatorHubSystemEventName.GIFT_DELIVERED,
  CreatorHubSystemEventName.GIFT_EXPIRED,
  CreatorHubSystemEventName.GIFT_WITHDRAWN,
];

/** The moments of a gift, which name their product rather than a collab. */
export const isGiftMarker = (event: string): boolean => GIFT_MARKERS.includes(event);

const KNOWN_SYSTEM_EVENTS: readonly string[] = DashboardConversationSystemEventNames;

/**
 * The wire can name a marker this build has never heard of — the two services
 * deploy independently. Everything downstream falls back to neutral rather than
 * leaking the raw name to the screen.
 */
export const isKnownSystemEvent = (event: string): boolean => KNOWN_SYSTEM_EVENTS.includes(event);

const glyphByName: Readonly<Record<string, SystemEventGlyph | undefined>> = SYSTEM_EVENT_GLYPHS;

export const getSystemEventGlyph = (event: string): SystemEventGlyph =>
  glyphByName[event] ?? InfoCircle;
